import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, X, Loader2, Sparkles, RefreshCw, Pencil, HandCoins } from "lucide-react";
import api, { CONDITIONS, DELIVERY, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prepareUpload } from "@/lib/imageCompress";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function PlaceAd() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "", price: "", category: "", subcategory: "", brand: "", condition: "gebruikt",
    delivery: "ophalen", shipping_price: "", city: "", description: "", allow_offers: true,
  });
  const [priceRange, setPriceRange] = useState(null); // { min, max } from AI
  const [aiState, setAiState] = useState("idle"); // idle | loading | done | error
  const [saving, setSaving] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const titleTouched = useRef(false);
  const submitLock = useRef(false);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);
  // Prefill the seller's known city from their profile.
  useEffect(() => { if (user?.city) setForm((f) => (f.city ? f : { ...f, city: user.city })); }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedCat = categories.find((c) => c.slug === form.category);
  const listingFee = selectedCat?.fee || 0;
  const subOptions = selectedCat?.subs || [];

  const onFiles = async (e) => {
    const files = Array.from(e.target.files).slice(0, 6 - images.length);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const v = await prepareUpload(file);
        const fd = new FormData();
        fd.append("files", v.cardBlob, "card.jpg");
        fd.append("files", v.fullBlob, "full.jpg");
        const { data } = await api.post("/uploads", fd);
        const [cardUrl, fullUrl] = data.urls;
        setImages((prev) => [...prev, { id: crypto.randomUUID(), src: cardUrl, full: fullUrl, blur: v.blur, ai: v.cardDataUrl }]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uploaden mislukt, probeer het opnieuw");
    } finally { setUploading(false); }
  };

  const removeImage = (id) => setImages((prev) => prev.filter((im) => im.id !== id));

  const generateAI = async () => {
    if (images.length === 0) return toast.error("Voeg eerst een foto toe");
    setAiState("loading");
    try {
      const { data } = await api.post("/ai/generate-listing", { images: images.map((im) => im.ai) });
      if (data.ok === false) {
        setAiState("error");
        toast.error(data.error || "AI genereren mislukt");
        return;
      }
      setForm((f) => ({
        ...f,
        // Never overwrite a title the user has already edited by hand.
        title: titleTouched.current ? f.title : (data.title || f.title),
        description: data.description || f.description,
        category: data.category || f.category,
        subcategory: data.category && data.category !== f.category ? (data.subcategory || "") : (data.subcategory || f.subcategory),
        price: data.price ? String(data.price) : f.price,
        brand: data.specs?.merk || data.specs?.Merk || f.brand,
        condition: data.specs?.staat || f.condition,
      }));
      if (data.price_min && data.price_max && data.price_max >= data.price_min) {
        setPriceRange({ min: data.price_min, max: data.price_max });
      }
      setAiState("done");
      toast.success(`AI-suggestie klaar${data.elapsed_ms ? ` (${(data.elapsed_ms / 1000).toFixed(1)}s)` : ""} — controleer en pas aan`);
    } catch (e) {
      setAiState("error");
      toast.error(e.response?.data?.detail || "AI genereren mislukt");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitLock.current || saving) return; // guard against double submits
    if (!form.title || !form.price || !form.category) return toast.error("Vul titel, prijs en categorie in");
    submitLock.current = true;
    setSaving(true);
    try {
      const { data } = await api.post("/listings", {
        title: form.title, price: parseFloat(form.price), category: form.category,
        subcategory: form.subcategory, description: form.description, brand: form.brand, condition: form.condition,
        delivery: form.delivery, shipping_price: parseFloat(form.shipping_price || 0),
        city: form.city, location: form.city, allow_offers: form.allow_offers,
        images: images.map((im) => im.src),
        images_full: images.map((im) => im.full),
        images_blur: images.map((im) => im.blur),
        specs: {},
      });
      // Paid categories (Auto's): the listing is saved as a draft and must be paid
      // before it goes live. Kick off the €10 Mollie checkout.
      if (data.requires_payment) {
        try {
          const { data: pay } = await api.post(`/listings/${data.id}/pay-fee`);
          if (pay.payments_enabled && pay.checkout_url) {
            window.location.href = pay.checkout_url;
            return;
          }
          toast.error("Betalen is tijdelijk niet beschikbaar. Je advertentie staat als concept in je profiel.");
          navigate("/profiel");
          return;
        } catch {
          toast.error("Betaling kon niet worden gestart. Je advertentie staat als concept in je profiel.");
          navigate("/profiel");
          return;
        }
      }
      toast.success("Advertentie geplaatst!");
      navigate(`/advertentie/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plaatsen mislukt. Probeer het opnieuw.");
      submitLock.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 md:px-8 md:pb-12" style={{ paddingBottom: "max(8rem, env(safe-area-inset-bottom))" }}>
      <h1 className="mb-1 font-display text-3xl text-[#F5F5F5] sm:text-4xl">PLAATS ADVERTENTIE</h1>
      <p className="mb-6 text-[#A0A0A0]">Gratis plaatsen. Zonder gedoe.</p>

      <form onSubmit={submit} className="space-y-6" data-testid="place-ad-form">
        <div>
          <label className="mb-2 block text-sm font-semibold text-[#F5F5F5]">Foto's</label>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {images.map((img, i) => (
              <div key={img.id} className="relative aspect-square overflow-hidden rounded-lg border border-[#333]">
                <img src={img.src} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => removeImage(img.id)} data-testid={`remove-image-${i}`} className="absolute right-1 top-1 rounded-full bg-[#111]/90 p-1 text-white"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {images.length < 6 && (
              <label data-testid="add-photo-btn" className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#444] bg-[#1c1c1c] text-[#A0A0A0] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6" />}
                <span className="text-xs">{uploading ? "Bezig..." : "Foto's toevoegen"}</span>
                <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="photo-input" />
              </label>
            )}
          </div>
        </div>

        {/* AI agent */}
        <div className="rounded-xl border border-[#FF6A00]/40 bg-[#FF6A00]/5 p-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#FF6A00]" />
            <p className="font-display text-lg text-[#F5F5F5]">AI ADVERTENTIE-AGENT</p>
          </div>
          <p className="mt-1 text-sm text-[#A0A0A0]">Upload foto's en laat AI de titel, categorie, beschrijving en prijs voorstellen. Jij controleert alles vóór publicatie.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={generateAI} disabled={aiState === "loading"} data-testid="ai-generate-btn" className="flex items-center gap-2 rounded-full bg-[#FF6A00] px-5 py-2.5 font-bold text-[#111] transition-all hover:bg-[#FFB27A] active:scale-95 disabled:opacity-60">
              {aiState === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {aiState === "loading" ? "Bezig..." : "Genereer met AI"}
            </button>
            {(aiState === "done" || aiState === "error") && (
              <button type="button" onClick={generateAI} disabled={aiState === "loading"} data-testid="ai-retry-btn" className="flex items-center gap-2 rounded-full border border-[#333] px-5 py-2.5 font-semibold text-[#F5F5F5] hover:border-[#FF6A00]"><RefreshCw className="h-4 w-4" /> Opnieuw genereren</button>
            )}
          </div>
          {aiState === "error" && <p className="mt-2 text-sm text-[#EF4444]" data-testid="ai-error">AI genereren mislukt. Probeer het opnieuw of vul zelf in.</p>}
          {aiState === "done" && <p className="mt-2 flex items-center gap-1 text-sm text-[#22C55E]"><Pencil className="h-3.5 w-3.5" /> Suggestie ingevuld — controleer en pas aan hieronder.</p>}
        </div>

        <Field label="Categorie">
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v, subcategory: "" }))}>
            <SelectTrigger data-testid="ad-category" className="border-[#333] bg-[#2A2A2A] text-[#F5F5F5]"><SelectValue placeholder="Kies een categorie" /></SelectTrigger>
            <SelectContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">
              {categories.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>
                  <span className="flex w-full items-center justify-between gap-4">
                    <span>{c.name}</span>
                    {c.fee > 0 && <span data-testid={`cat-fee-${c.slug}`} className="font-bold text-[#FF6A00]">€{c.fee}</span>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {subOptions.length > 0 && (
          <Field label="Subcategorie (optioneel)">
            <Select value={form.subcategory || "__none"} onValueChange={(v) => set("subcategory", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="ad-subcategory" className="border-[#333] bg-[#2A2A2A] text-[#F5F5F5]"><SelectValue placeholder="Kies een subcategorie" /></SelectTrigger>
              <SelectContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">
                <SelectItem value="__none">Geen subcategorie</SelectItem>
                {subOptions.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}

        {listingFee > 0 && (
          <div data-testid="fee-notice" className="rounded-xl border border-[#FF6A00]/40 bg-[#FF6A00]/5 p-4 text-sm text-[#F5F5F5]">
            <p className="font-semibold text-[#FF6A00]">Plaatsingskosten: €{listingFee}</p>
            <p className="mt-1 text-[#C2C2C2]">Advertenties in deze categorie kosten eenmalig €{listingFee} en staan 4 weken online. Je betaalt na het controleren, bij het definitief plaatsen. Zonder betaling blijft je advertentie een concept.</p>
          </div>
        )}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-semibold text-[#F5F5F5]">Titel</label>
            <button type="button" onClick={() => setEditTitle(true)} data-testid="edit-title-btn" className="flex items-center gap-1 text-xs font-semibold text-[#FF6A00] hover:underline"><Pencil className="h-3 w-3" /> Titel aanpassen</button>
          </div>
          <input data-testid="ad-title" value={form.title} onChange={(e) => { titleTouched.current = true; set("title", e.target.value); }} placeholder="Bijv. Volkswagen Golf 1.4 TSI" className={inputCls} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-[#F5F5F5]">Prijs (€)</label>
            <input data-testid="ad-price" type="number" min="0" inputMode="decimal" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="0" className={inputCls} />
            {priceRange && <p className="mt-1 text-xs text-[#FFB27A]" data-testid="ai-price-range">AI-schatting: {formatPrice(priceRange.min)} – {formatPrice(priceRange.max)}</p>}
          </div>
          <Field label="Merk"><input data-testid="ad-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Optioneel" className={inputCls} /></Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Staat">
            <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger data-testid="ad-condition" className="border-[#333] bg-[#2A2A2A] text-[#F5F5F5]"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">{CONDITIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Levering">
            <Select value={form.delivery} onValueChange={(v) => set("delivery", v)}>
              <SelectTrigger data-testid="ad-delivery" className="border-[#333] bg-[#2A2A2A] text-[#F5F5F5]"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]">{DELIVERY.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Plaats"><CityAutocomplete value={form.city} onChange={(v) => set("city", v)} inputCls={inputCls} /></Field>
          {form.delivery !== "ophalen" && <Field label="Verzendkosten (€)"><input data-testid="ad-shipping" type="number" min="0" step="0.01" inputMode="decimal" value={form.shipping_price} onChange={(e) => set("shipping_price", e.target.value)} placeholder="0" className={inputCls} /></Field>}
        </div>

        {/* Bieden toestaan */}
        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-[#333] bg-[#1c1c1c] p-4" data-testid="allow-offers-row">
          <span className="flex items-center gap-3">
            <HandCoins className="h-5 w-5 text-[#FF6A00]" />
            <span>
              <span className="block font-semibold text-[#F5F5F5]">Bieden toestaan</span>
              <span className="block text-xs text-[#A0A0A0]">Kopers kunnen een bod uitbrengen op je advertentie.</span>
            </span>
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={form.allow_offers}
            data-testid="allow-offers-toggle"
            onClick={() => set("allow_offers", !form.allow_offers)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors ${form.allow_offers ? "bg-[#FF6A00]" : "bg-[#3a3a3a]"}`}
          >
            <span className={`pointer-events-none block h-5 w-5 rounded-full bg-white shadow transition-transform ${form.allow_offers ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </label>

        <Field label="Beschrijving">
          <textarea data-testid="ad-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={5} maxLength={1000} placeholder="Beschrijf je artikel..." className={`${inputCls} resize-none`} />
          <p className="mt-1 text-right text-xs text-[#777]">{form.description.length}/1000</p>
        </Field>

        <button type="submit" disabled={saving} data-testid="ad-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-4 font-bold text-[#111] transition-all hover:bg-[#FFB27A] active:scale-95 disabled:opacity-60">
          {saving && <Loader2 className="h-5 w-5 animate-spin" />} {saving ? "Bezig..." : listingFee > 0 ? `BETAAL €${listingFee} & PLAATS` : "PUBLICEREN"}
        </button>
      </form>

      <TitleEditDialog
        open={editTitle}
        initial={form.title}
        onClose={() => setEditTitle(false)}
        onSave={(v) => { titleTouched.current = true; set("title", v); setEditTitle(false); }}
      />
    </div>
  );
}

function TitleEditDialog({ open, initial, onClose, onSave }) {
  const [val, setVal] = useState(initial);
  useEffect(() => { if (open) setVal(initial); }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="title-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Titel aanpassen</DialogTitle></DialogHeader>
        <textarea
          autoFocus
          data-testid="title-edit-input"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          rows={3}
          maxLength={80}
          className="w-full rounded-lg border border-[#333] bg-[#2A2A2A] p-3 text-lg text-[#F5F5F5] outline-none focus:border-[#FF6A00]"
        />
        <p className="text-right text-xs text-[#777]">{val.length}/80</p>
        <DialogFooter className="gap-2">
          <button type="button" onClick={onClose} data-testid="title-edit-cancel" className="rounded-full border border-[#333] px-5 py-2.5 font-semibold text-[#F5F5F5] hover:border-[#FF6A00]">Annuleren</button>
          <button type="button" onClick={() => onSave(val.trim())} data-testid="title-edit-save" className="rounded-full bg-[#FF6A00] px-6 py-2.5 font-bold text-[#111]">Opslaan</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "w-full rounded-lg border border-[#333] bg-[#2A2A2A] p-3 text-[#F5F5F5] outline-none placeholder:text-[#777] focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00]";
function Field({ label, children }) { return <div><label className="mb-2 block text-sm font-semibold text-[#F5F5F5]">{label}</label>{children}</div>; }
